import {
  AsyncAutocompleteWithAdd,
  AsyncCallArgs,
  FormSection,
  GridContainer,
  GridItem,
  handleFormSubmission,
  InputFormField,
  isArrayNotEmpty,
  isNotEmpty,
  SubmitFormButton,
  useAsyncCall,
  useReactHookForm,
  ViewDialog,
} from "@duosoftbg/nacid-components";
import { FormProvider } from "react-hook-form";
import { Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { closeEditModal } from "../../../../../../../../store/redux/slice/ComponentsControl/adminConsoleConfigControl";
import useAppDispatch from "../../../../../../../../hooks/redux/base/useAppDispatch";
import { adminConsoleSection } from "../../../../../../../../utils/constants";
import useAppSelector from "../../../../../../../../hooks/redux/base/useAppSelector";
import { useTranslation } from "react-i18next";
import { AdminConsoleSectionDetails } from "../../../../../../../../types/adminConsoleTypes";
import { toast } from "react-toastify";
import { createAcSection, editAcSection, getRolesAutocomplete } from "../../../../../../../../axios/api/services";
import { acSectionCreationInitialValues } from "../../../../../../../../init/adminConsoleCreationInitialValues";
import RolesDialogList from "../../../../../groupsAndRoles/dialog/parts/RolesDialogList";
import { renewAcCache } from "../../../../../../../../store/redux/slice/AppData/aConsoleData";
import { createSectionValidationSchema } from "../../../../../../../../yup/schema/adminConsole/adminConsoleValidationSchema";

const AdminConsoleSectionDialog = () => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState([]);

  const { open, object } = useAppSelector((state) => {
    return state.ComponentsControl.acConfigControl.sections[adminConsoleSection.section].modals.edit;
  });

  const isEdit = isNotEmpty(object?.id);

  const { methods } = useReactHookForm<AdminConsoleSectionDetails>({
    defaultValues: undefined,
    validationSchema: createSectionValidationSchema,
  });

  useEffect(() => {
    if (object) {
      methods.reset(object);

      if (open && object.id) {
        let roles = isArrayNotEmpty(object.accessRoles)
          ? object.accessRoles.map((role) => ({ id: role, name: role }))
          : [];
        setRoles(roles);
      }
    }
  }, [methods, object, open]);

  const handleCloseDialog = () => {
    methods.reset(acSectionCreationInitialValues);
    setRoles([]);
    dispatch(closeEditModal({ section: adminConsoleSection.section }));
  };

  const onSubmit = (values) => {
    const createSection: AsyncCallArgs = {
      promise: isEdit ? editAcSection(values, roles) : createAcSection(values, roles),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        dispatch(renewAcCache(response));
        handleCloseDialog();
        toast.success(isEdit ? t("m.ac.section.update.success") : t("m.ac.section.create.success"));
      },
    };
    asyncCall(createSection);
  };

  const handleAddRole = (role) => {
    if (!roles.find((arrayRole) => arrayRole.name === role.name)) {
      setRoles([...roles, { id: role.name, name: role.name }]);
    }
  };

  const handleRemoveRoleClick = (role) => {
    setRoles(roles.filter((arrayRole) => arrayRole.id !== role.id));
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={isEdit ? "t.ac.section.update.dialog" : "t.ac.section.create.dialog"}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmission({ methods, onSubmitFn: onSubmit, t })}>
          {isEdit && (
            <GridContainer spacing={3}>
              <GridItem sm={12} md={4}>
                <InputFormField isDisabled={true} required={true} fieldName={"id"} labelCode={"l.input.ac.id"} />
              </GridItem>
            </GridContainer>
          )}

          <FormSection label={"t.main.details"}>
            <GridContainer spacing={3} mt={0}>
              <GridItem sm={12} md={4}>
                <InputFormField fieldName={"title"} labelCode={"l.input.ac.section.title"} />
              </GridItem>
              <GridItem sm={12} md={4}>
                <InputFormField fieldName={"titleEn"} labelCode={"l.input.ac.section.title.en"} />
              </GridItem>
              <GridItem sm={12} md={4}>
                <InputFormField required={true} fieldName={"position"} labelCode={"l.input.ac.position"} />
              </GridItem>
            </GridContainer>
          </FormSection>

          <FormSection label={"t.ac.roles"}>
            <GridContainer spacing={3} mt={0}>
              <AsyncAutocompleteWithAdd
                onAddRecord={handleAddRole}
                setOptionText={(option) => option.name}
                getOptionLabel={(option) => option.id}
                setInputOnSelect={(option) => option.name}
                autocompleteFn={getRolesAutocomplete}
                label={"l.select.role"}
              />
              <RolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
            </GridContainer>
          </FormSection>

          <GridContainer spacing={3} mt={0}>
            <GridItem sm={12} md={12}>
              <Typography align={"right"}>
                <SubmitFormButton label={"l.btn.submit"} color={"success"} />
              </Typography>
            </GridItem>
          </GridContainer>
        </form>
      </FormProvider>
    </ViewDialog>
  );
};
export default AdminConsoleSectionDialog;
